/**
 * webCOA runtime configuration.
 *
 * Unlike upstream webAO -- which reads its server, asset base and theme
 * from URL query parameters and a master-server picker -- webCOA is a
 * single-server client. Everything that an operator might need to change
 * after a build lives in `config.json`, served next to `client.html`.
 * Relocating the whole app to a different subfolder, or repointing the
 * asset host, is a one-file edit with no rebuild.
 *
 * Two distinct "bases" exist and must not be confused:
 *   - the APP base: where client.html, the JS bundles, CSS, lib/ and
 *     golden/ live (e.g. https://mastyra.com/courtofattrition/web/).
 *     Derived at runtime so the build is path-agnostic.
 *   - the ASSET base: where character/background/sound assets live
 *     (https://mastyra.com/base/). Absolute and authoritative.
 */

export interface WebcoaConfig {
  /** Window/title + handshake server name. */
  serverName: string;
  /** WS endpoints. `secure` is used on https: pages, `insecure` on http:. */
  connection: {
    secure: { host: string; port: number };
    insecure: { host: string; port: number };
  };
  /** Absolute base for character/bg/sfx assets. MUST end in a slash. */
  assetBaseUrl: string;
  /** Theme folder name under assets (and the bundled fallback theme). */
  defaultTheme: string;
  /**
   * Subdirectory the app is served from. "auto" derives it from the
   * location of client.html at runtime, which is what you want for a
   * drop-in Apache subfolder. An explicit value (e.g.
   * "/courtofattrition/web/") overrides the derivation.
   */
  deployBasePath: string;
  oocImages: {
    enabled: boolean;
    /** Only URLs with one of these protocols are ever embedded. */
    allowedProtocols: string[];
  };
}

/** Safe defaults: used only if config.json is missing or malformed. */
const DEFAULTS: WebcoaConfig = {
  serverName: "Court of Attrition",
  connection: {
    secure: { host: "www.mastyra.com", port: 443 },
    insecure: { host: "www.mastyra.com", port: 50001 },
  },
  assetBaseUrl: "https://mastyra.com/base/",
  defaultTheme: "default",
  deployBasePath: "auto",
  oocImages: { enabled: true, allowedProtocols: ["https:"] },
};

let loaded: WebcoaConfig | null = null;

/** Ensures a string ends with exactly one trailing slash. */
function withTrailingSlash(s: string): string {
  return s.endsWith("/") ? s : s + "/";
}

/**
 * Resolves the APP base URL at runtime. With deployBasePath "auto" we
 * take the directory of the current document, which is correct whether
 * the app is at the web root or in any subfolder, on any host. An
 * explicit deployBasePath is treated as a path on the current origin.
 */
export function resolveAppBase(cfg: WebcoaConfig): string {
  if (cfg.deployBasePath && cfg.deployBasePath !== "auto") {
    const path = withTrailingSlash(cfg.deployBasePath);
    return new URL(path, window.location.origin).href;
  }
  // Directory of the current page (strip the filename component).
  const url = new URL(window.location.href);
  url.search = "";
  url.hash = "";
  url.pathname = url.pathname.replace(/[^/]*$/, "");
  return withTrailingSlash(url.href);
}

/**
 * Builds the WebSocket URL with no mixed-content risk: on an https: page
 * we always use wss: to the secure endpoint; only a plain http: page
 * (dev) falls back to ws: on the insecure port. The served build is
 * always https:, so it is always wss:.
 */
export function resolveWebSocketUrl(cfg: WebcoaConfig): string {
  if (window.location.protocol === "https:") {
    const { host, port } = cfg.connection.secure;
    return `wss://${host}:${port}`;
  }
  const { host, port } = cfg.connection.insecure;
  return `ws://${host}:${port}`;
}

/**
 * Forces an asset base to https: when the page is https:, so a stray
 * http:// in config (or a server-sent ASS packet, if we ever honor one)
 * can never produce a blocked mixed-content request.
 */
export function normalizeAssetBase(rawBase: string): string {
  let base = withTrailingSlash(rawBase.trim());
  if (window.location.protocol === "https:" && base.startsWith("http://")) {
    base = "https://" + base.slice("http://".length);
  }
  return base;
}

/**
 * Loads and caches config.json (fetched relative to the document, so it
 * works under any subpath). Call once during boot, before connecting.
 */
export async function loadConfig(): Promise<WebcoaConfig> {
  if (loaded) return loaded;
  let cfg: WebcoaConfig = { ...DEFAULTS };
  try {
    const res = await fetch("config.json", { cache: "no-cache" });
    if (res.ok) {
      const json = (await res.json()) as Partial<WebcoaConfig>;
      cfg = {
        ...DEFAULTS,
        ...json,
        connection: { ...DEFAULTS.connection, ...(json.connection ?? {}) },
        oocImages: { ...DEFAULTS.oocImages, ...(json.oocImages ?? {}) },
      };
    } else {
      console.warn(`config.json returned ${res.status}; using defaults`);
    }
  } catch (e) {
    console.warn("config.json fetch failed; using defaults", e);
  }
  cfg.assetBaseUrl = normalizeAssetBase(cfg.assetBaseUrl);
  loaded = cfg;
  return cfg;
}

/** Synchronous accessor for code that runs after loadConfig() resolved. */
export function getConfig(): WebcoaConfig {
  if (!loaded) {
    throw new Error("getConfig() called before loadConfig() resolved");
  }
  return loaded;
}

/**
 * Ordered placeholder-sprite candidates for a fully-missing emote: the
 * server's default-theme placeholder on the ASSET base first (probing the
 * common extensions, e.g. mastyra.com/base/themes/default/placeholder.gif),
 * then the placeholder bundled with the build as a last resort. This is
 * what the desktop client uses for missing/custom characters.
 */
export function getThemePlaceholderUrls(): string[] {
  const cfg = getConfig();
  const theme = cfg.defaultTheme;
  const exts = [".gif", ".png", ".webp", ".apng"];
  const urls = exts.map(
    (e) => `${cfg.assetBaseUrl}themes/${theme}/placeholder${e}`,
  );
  // Bundled fallback (app base) if the server has no theme placeholder.
  urls.push(`${resolveAppBase(cfg)}themes/${theme}/placeholder.png`);
  return urls;
}

/**
 * URL of the placeholder sprite bundled with the build (under the app
 * base). Kept for callers that want a single guaranteed-present fallback.
 */
export function getThemePlaceholderUrl(): string {
  const cfg = getConfig();
  return `${resolveAppBase(cfg)}themes/${cfg.defaultTheme}/placeholder.png`;
}
